// react
import React, { useState, useEffect, useMemo } from "react";

// react-router-dom
import { useNavigate } from "react-router-dom";

// prop-types
import PropTypes from "prop-types";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, Search as SearchIcon, Add as AddIcon } from "@mui/icons-material";

// utils
import convertTimestamp from "utils/convertTimestamp";

// config
import documentMapping from "../../config/documentMapping.json";

// A ---------------------------------------------------------------------- M

const DataBoxesTableToolbar = ({ handleOpen, searchQuery, handleSearchChange }) => {
  const { canCreateDatabox, conservSostL1 } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" fontWeight={700}>
          {conservSostL1 ? t("my_archives") : t("my_databoxes")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} container justifyContent="flex-end" spacing={1}>
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            placeholder={`${t("search_by_name")}...`}
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                "&:hover fieldset": { borderColor: theme.palette.primary.main },
              },
            }}
          />
        </Grid>

        {!isMobile && canCreateDatabox && (
          <Grid item sm="auto">
            <Button
              onClick={handleOpen}
              startIcon={<AddIcon />}
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {t("databox")}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const DataBoxesTable = ({ tags, handleOpen }) => {
  const { conservSostL1 } = UserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTags = useMemo(() => tags.filter((tag) => tag.name.toLowerCase().includes(searchQuery.toLowerCase())), [tags, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, tags]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDocumentTypeLabel = (code) => documentMapping[code] || code;

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredTags.length) : 0;

  return (
    <Box>
      <DataBoxesTableToolbar handleOpen={handleOpen} searchQuery={searchQuery} handleSearchChange={(e) => setSearchQuery(e.target.value)} />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
          backdropFilter: "blur(6px)",
          border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table data-testid="databoxes-table">
          {!isMobile && (
            <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t("name")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("registration_datetime")}</TableCell>
                {conservSostL1 && <TableCell sx={{ fontWeight: 700 }}>{t("document_type")}</TableCell>}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {(rowsPerPage > 0 ? filteredTags.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : filteredTags).map((tag) =>
              isMobile ? (
                <Box
                  key={tag.id}
                  onClick={() => navigate(`/${tag.id}`)}
                  sx={{
                    p: 2,
                    mb: 2,
                    mx: 1,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.01)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {tag.name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {convertTimestamp(tag.firstseen)}
                  </Typography>

                  {conservSostL1 && (
                    <Box mt={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Chip
                        label={getDocumentTypeLabel(tag.tipologiaDocumentale)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: "8px",
                          backgroundColor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                          textTransform: "capitalize",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <TableRow
                  key={tag.id}
                  onClick={() => navigate(`/${tag.id}`)}
                  sx={{
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main}15`,
                      transform: "scale(1.002)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  <TableCell>{tag.name}</TableCell>

                  <TableCell>{convertTimestamp(tag.firstseen)}</TableCell>

                  {conservSostL1 && (
                    <TableCell>
                      <Chip
                        label={getDocumentTypeLabel(tag.tipologiaDocumentale)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: "8px",
                          backgroundColor: `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              )
            )}
            {!isMobile && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={conservSostL1 ? 3 : 2} />
              </TableRow>
            )}
          </TableBody>

          {!isMobile && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  count={filteredTags.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>

        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <TablePaginationActions count={filteredTags.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

DataBoxesTable.propTypes = {
  tags: PropTypes.array.isRequired,
  handleOpen: PropTypes.func.isRequired,
};

export default DataBoxesTable;
